import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { 
  ChevronLeft, ChevronRight, Bookmark, BookmarkPlus, Settings, Book,
  Minus, Plus, Eye, EyeOff, Crown, FileText, Highlighter, StickyNote, X, Bell, Share2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Separator } from "../components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { toast } from "sonner";
import AudioPlayer from "../components/AudioPlayer";
import FormattedCommentary from "../components/FormattedCommentary";
import { useAuth } from "../contexts/AuthContext";
import { 
  supabase,
  getBooks, getBook, getChapter, 
  getGardePages, createGardePage, 
  getSurlignages, createSurlignage, deleteSurlignage 
} from "../lib/supabase";

const ReaderPage = () => {
  const { bookId = "genese", chapter = "1" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [books, setBooks] = useState({ ancien_testament: [], nouveau_testament: [] });
  const [currentBook, setCurrentBook] = useState(null);
  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [booksOpen, setBooksOpen] = useState(false);
  const [selectedSidebarBook, setSelectedSidebarBook] = useState(bookId);

  useEffect(() => {
    if (bookId) {
      setSelectedSidebarBook(bookId);
    }
  }, [bookId]);
  
  // Garde-page dialog
  const [showGardePageDialog, setShowGardePageDialog] = useState(false);
  const [gardePageName, setGardePageName] = useState("");
  const [gardePageCount, setGardePageCount] = useState(0);
  const [gardePageVerse, setGardePageVerse] = useState(null);
  
  // Surlignages from server
  const [surlignages, setSurlignages] = useState([]);
  const [highlightMenuVerse, setHighlightMenuVerse] = useState(null);
  // Selected text segments, possibly spanning several verses:
  // [{ verseNum, startOffset, endOffset, text }]
  const [selectionSegments, setSelectionSegments] = useState(null);
  
  // Local notes
  const [userNotes, setUserNotes] = useState(() => {
    const saved = localStorage.getItem("bible-notes");
    return saved ? JSON.parse(saved) : {};
  });
  const [activeNoteVerse, setActiveNoteVerse] = useState(null);
  const [noteText, setNoteText] = useState("");
  
  // Introduction
  const [introduction, setIntroduction] = useState("");
  const [showIntro, setShowIntro] = useState(false);
  
  // Premium status from Auth Context
  const { user, isPremium } = useAuth();
  
  // User ID - strictly use Supabase auth UUID (no fallback)
  // Premium features (surlignages/garde-pages) require a valid authenticated UUID
  // because Supabase RLS expects auth.uid() to match user_id (UUID type)
  const userId = user?.id || null;
  
  // Reading settings
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem("bible-font-size");
    return saved ? parseInt(saved) : 18;
  });
  const [hideNumbers, setHideNumbers] = useState(() => {
    return localStorage.getItem("bible-hide-numbers") === "true";
  });
  const [hideTitles, setHideTitles] = useState(() => {
    return localStorage.getItem("bible-hide-titles") === "true";
  });
  const [focusMode, setFocusMode] = useState(() => {
    return localStorage.getItem("bible-focus-mode") === "true";
  });
  const [showComments, setShowComments] = useState(() => {
    const saved = localStorage.getItem("bible-show-comments");
    return saved !== null ? saved === "true" : true;
  });

  const chapterNum = parseInt(chapter);

  // Save settings
  useEffect(() => {
    localStorage.setItem("bible-font-size", fontSize.toString());
  }, [fontSize]);
  useEffect(() => {
    localStorage.setItem("bible-hide-numbers", hideNumbers.toString());
  }, [hideNumbers]);
  useEffect(() => {
    localStorage.setItem("bible-hide-titles", hideTitles.toString());
  }, [hideTitles]);
  useEffect(() => {
    localStorage.setItem("bible-show-comments", showComments.toString());
  }, [showComments]);
  useEffect(() => {
    localStorage.setItem("bible-focus-mode", focusMode.toString());
    // Block/unblock notifications when focus mode changes
    if (focusMode && 'Notification' in window && Notification.permission === 'granted') {
      // Store original notification constructor
      window._originalNotification = window.Notification;
      // Override to block notifications
      window.Notification = function() { return {}; };
      window.Notification.permission = 'denied';
    } else if (!focusMode && window._originalNotification) {
      // Restore original notification
      window.Notification = window._originalNotification;
    }
  }, [focusMode]);

  // Fetch books from Supabase
  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const data = await getBooks();
        setBooks(data);
      } catch (error) {
        console.error("Error fetching books:", error);
      }
    };
    fetchBooks();
  }, []);

  // Find current book and load introduction
  useEffect(() => {
    const allBooks = [...books.ancien_testament, ...books.nouveau_testament];
    const book = allBooks.find(b => b.id === bookId);
    setCurrentBook(book);
    
    const loadBookDetails = async () => {
      try {
        const bookData = await getBook(bookId);
        setIntroduction(bookData.introduction || "");
      } catch (e) {
        setIntroduction("");
      }
    };
    if (bookId) loadBookDetails();
  }, [books, bookId]);

  // Fetch chapter content from Supabase
  useEffect(() => {
    const fetchChapter = async () => {
      setLoading(true);
      try {
        const data = await getChapter(bookId, chapterNum);
        setVerses(data.verses);
      } catch (error) {
        console.error("Error fetching chapter:", error);
        setVerses([]);
      } finally {
        setLoading(false);
      }
    };
    fetchChapter();
  }, [bookId, chapterNum]);

  // Fetch garde-pages count
  useEffect(() => {
    const fetchGardePages = async () => {
      if (!isPremium || !userId) return;
      try {
        const data = await getGardePages(userId);
        setGardePageCount(data.length);
      } catch (error) {
        console.error("Error fetching garde-pages:", error);
      }
    };
    fetchGardePages();
  }, [userId, isPremium]);

  // Fetch surlignages for current chapter
  useEffect(() => {
    const fetchSurlignages = async () => {
      if (!isPremium || !userId) return;
      try {
        const data = await getSurlignages(userId, bookId);
        setSurlignages(data.filter(s => s.chapter === chapterNum));
      } catch (error) {
        console.error("Error fetching surlignages:", error);
      }
    };
    fetchSurlignages();
  }, [userId, bookId, chapterNum, isPremium]);

  // Scroll to verse if coming from search/garde-page OR from a deep link (?verse=X)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const verseFromUrl = params.get("verse");
    const targetVerse = location.state?.scrollToVerse || (verseFromUrl ? parseInt(verseFromUrl, 10) : null);

    if (targetVerse && !loading) {
      setTimeout(() => {
        const element = document.querySelector(`[data-testid="verse-${targetVerse}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('bg-gold-leaf/20');
          setTimeout(() => element.classList.remove('bg-gold-leaf/20'), 2500);
        }
      }, 300);
    }
  }, [location.state, location.search, loading]);

  // Share verse via Web Share API (deep link), with clipboard fallback
  const shareVerse = async (verseNum) => {
    const verse = verses.find(v => v.verse === verseNum);
    if (!verse) return;

    const bookName = currentBook?.name || bookId;
    const url = `${window.location.origin}/bible/${bookId}/${chapterNum}?verse=${verseNum}`;
    const title = `${bookName} ${chapterNum}:${verseNum} — Bible Fillion 1920`;
    const text = `📖 Bible Fillion 1920\n\n« ${verse.text} »\n\n— ${bookName} ${chapterNum}, ${verseNum}\n\n`;

    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(`${text}${url}`);
        toast.success("Verset copié dans le presse-papier");
      } else {
        toast.info("Partage non supporté sur ce navigateur");
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        console.error("Share failed:", err);
        toast.error("Impossible de partager le verset");
      }
    }
  };

  // Save garde-page
  const saveGardePage = async (verseNum = null) => {
    if (!isPremium) {
      toast.error("Fonctionnalité Premium", {
        description: "Les garde-pages sont réservés aux utilisateurs Premium.",
        action: { label: "Débloquer", onClick: () => navigate("/premium") }
      });
      setShowGardePageDialog(false);
      return;
    }

    if (!userId) {
      toast.error("Connexion requise", {
        description: "Veuillez vous connecter pour enregistrer un garde-page.",
        action: { label: "Se connecter", onClick: () => navigate("/auth") }
      });
      setShowGardePageDialog(false);
      return;
    }
    
    if (!gardePageName.trim()) {
      toast.error("Veuillez entrer un nom pour le garde-page");
      return;
    }
    
    const verseToSave = verseNum || gardePageVerse;
    
    try {
      await createGardePage({
        user_id: userId,
        name: gardePageName,
        book_id: bookId,
        chapter: chapterNum,
        verse: verseToSave,
        scroll_position: window.scrollY / document.documentElement.scrollHeight
      });
      
      const verseInfo = verseToSave ? ` (verset ${verseToSave})` : '';
      toast.success("Garde-page enregistré !", {
        description: `${currentBook?.name} chapitre ${chapter}${verseInfo}`
      });
      setShowGardePageDialog(false);
      setGardePageName("");
      setGardePageVerse(null);
      setGardePageCount(prev => prev + 1);
    } catch (error) {
      console.error("Error saving garde-page:", error);
      // Show the actual error message to help diagnose issues quickly
      const msg = error?.message || error?.error_description || "Erreur inconnue";
      toast.error("Impossible d'enregistrer le garde-page", {
        description: msg,
        duration: 7000,
      });
    }
  };

  const openGardePageDialog = (verseNum = null) => {
    setGardePageVerse(verseNum);
    const verseSuffix = verseNum ? ` - v.${verseNum}` : '';
    setGardePageName(`Garde-page ${gardePageCount + 1}${verseSuffix}`);
    setShowGardePageDialog(true);
  };

  // Highlight colors
  const highlightColors = [
    { name: "yellow", label: "Jaune", color: "#FEF08A" },
    { name: "green", label: "Vert", color: "#BBF7D0" },
    { name: "blue", label: "Bleu", color: "#BFDBFE" },
    { name: "pink", label: "Rose", color: "#FBCFE8" },
    { name: "orange", label: "Orange", color: "#FED7AA" },
  ];

  const getHighlights = (verseNum) => {
    return surlignages.filter(s => 
      s.verse_start === verseNum || 
      (s.verse_end && verseNum >= s.verse_start && verseNum <= s.verse_end)
    );
  };

  // Compute the selected text segments across one or several verses.
  // Returns [{ verseNum, startOffset, endOffset, text }] or null.
  const computeSelectionSegments = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null;
    const range = selection.getRangeAt(0);
    const segments = [];

    document.querySelectorAll('[data-verse-text]').forEach((el) => {
      let intersects = false;
      try {
        intersects = range.intersectsNode(el);
      } catch (e) {
        intersects = false;
      }
      if (!intersects) return;

      const verseNum = parseInt(el.getAttribute('data-verse-text'), 10);
      const verse = verses.find((v) => v.verse === verseNum);
      if (!verse) return;

      const elRange = document.createRange();
      elRange.selectNodeContents(el);

      // Offset of the selection start within this verse's text
      const startRange = elRange.cloneRange();
      if (range.compareBoundaryPoints(Range.START_TO_START, elRange) > 0) {
        startRange.setEnd(range.startContainer, range.startOffset);
      } else {
        startRange.collapse(true);
      }
      const startOffset = startRange.toString().length;

      // Offset of the selection end within this verse's text
      const endRange = elRange.cloneRange();
      if (range.compareBoundaryPoints(Range.END_TO_END, elRange) < 0) {
        endRange.setEnd(range.endContainer, range.endOffset);
      }
      const endOffset = endRange.toString().length;

      if (endOffset > startOffset) {
        segments.push({
          verseNum,
          startOffset,
          endOffset,
          text: verse.text.substring(startOffset, endOffset),
        });
      }
    });

    return segments.length > 0 ? segments.sort((a, b) => a.verseNum - b.verseNum) : null;
  };

  const captureSelection = () => {
    // Small delay so the browser finalizes the selection (especially on mobile)
    setTimeout(() => {
      const segments = computeSelectionSegments();
      if (segments) setSelectionSegments(segments);
    }, 60);
  };

  const clearSelection = () => {
    try {
      window.getSelection().removeAllRanges();
    } catch (e) {
      // ignore
    }
    setSelectionSegments(null);
  };

  // Share the current (possibly multi-verse) selection
  const shareSelection = async () => {
    if (!selectionSegments || selectionSegments.length === 0) return;
    const first = selectionSegments[0].verseNum;
    const last = selectionSegments[selectionSegments.length - 1].verseNum;
    const bookName = currentBook?.name || bookId;
    const ref = first === last ? `${chapterNum}, ${first}` : `${chapterNum}, ${first}-${last}`;
    const url = `${window.location.origin}/bible/${bookId}/${chapterNum}?verse=${first}`;
    const title = `${bookName} ${chapterNum}:${first}${first === last ? '' : `-${last}`} — Bible Fillion 1920`;
    const body = selectionSegments.map((s) => s.text).join(' ');
    const text = `📖 Bible Fillion 1920\n\n« ${body} »\n\n— ${bookName} ${ref}\n\n`;

    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(`${text}${url}`);
        toast.success("Sélection copiée dans le presse-papier");
      } else {
        toast.info("Partage non supporté sur ce navigateur");
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        console.error("Share failed:", err);
        toast.error("Impossible de partager la sélection");
      }
    }
  };

  const applySegmentsHighlight = async (segments, colorName) => {
    if (segments.length === 0) return;
    
    // Sort segments by verse number just in case
    segments.sort((a, b) => a.verseNum - b.verseNum);
    
    const startSeg = segments[0];
    const endSeg = segments[segments.length - 1];
    
    // Join text for multi-verse
    const fullText = segments.map(s => s.text).join(' [...] ');
    
    const created = await createSurlignage({
      user_id: userId,
      book_id: bookId,
      chapter: chapterNum,
      verse_start: startSeg.verseNum,
      verse_end: endSeg.verseNum,
      text: fullText,
      selected_text: fullText,
      start_offset: startSeg.startOffset,
      end_offset: endSeg.endOffset,
      color: colorName,
    });
    
    setSurlignages((prev) => [...prev, created]);
    toast.success(
      segments.length > 1 ? `Passage surligné (${segments.length} versets)` : "Passage surligné"
    );
    clearSelection();
  };

  const setHighlight = async (verseNum, colorName, colorHex) => {
    if (!isPremium) {
      toast.error("Fonctionnalité Premium", {
        description: "Le surlignage est réservé aux utilisateurs Premium.",
        action: { label: "Débloquer", onClick: () => navigate("/premium") }
      });
      setHighlightMenuVerse(null);
      clearSelection();
      return;
    }

    if (!userId) {
      toast.error("Connexion requise", {
        description: "Veuillez vous connecter pour surligner un passage.",
        action: { label: "Se connecter", onClick: () => navigate("/auth") }
      });
      setHighlightMenuVerse(null);
      clearSelection();
      return;
    }
    
    if (colorHex) {
      // Use the captured segments (may span several verses), or try to
      // capture the live selection one more time.
      let segments = selectionSegments;
      if (!segments || segments.length === 0) {
        segments = computeSelectionSegments();
      }

      if (!segments || segments.length === 0) {
        toast.error("Veuillez sélectionner du texte d'abord", {
          description: "Surlignez le passage que vous souhaitez marquer avant de choisir une couleur"
        });
        setHighlightMenuVerse(null);
        return;
      }

      try {
        await applySegmentsHighlight(segments, colorName);
      } catch (error) {
        console.error("Error saving highlight:", error);
        const msg = error?.message || "Erreur inconnue";
        toast.error("Impossible de surligner", { description: msg, duration: 7000 });
      }
    } else {
      // Remove all highlights for this verse
      const verseSurlignages = surlignages.filter(s => 
        s.verse_start === verseNum || 
        (s.verse_end && verseNum >= s.verse_start && verseNum <= s.verse_end)
      );
      if (verseSurlignages.length > 0) {
        try {
          await Promise.all(verseSurlignages.map(s => deleteSurlignage(s.id)));
          setSurlignages(prev => prev.filter(s => !(s.verse_start === verseNum || (s.verse_end && verseNum >= s.verse_start && verseNum <= s.verse_end))));
          toast.success("Surlignages retirés");
        } catch (error) {
          console.error("Error deleting highlights:", error);
          toast.error("Erreur lors de la suppression");
        }
      } else {
        toast.info("Aucun surlignage à supprimer");
      }
    }
    setHighlightMenuVerse(null);
  };

  const getHighlightKey = (verseNum) => `${bookId}-${chapter}-${verseNum}`;
  const getNote = (verseNum) => userNotes[getHighlightKey(verseNum)];

  // Render verse text with partial highlights
    const renderVerseWithHighlights = (verse) => {
    const text = verse.text || "";
    const highlights = getHighlights(verse.verse);
    
    if (highlights.length === 0) {
      return <span>{text}</span>;
    }
    
    // Sort highlights by start position
    const sortedHighlights = [...highlights].sort((a, b) => 
      (a.start_offset || 0) - (b.start_offset || 0)
    );
    
    const parts = [];
    let lastIndex = 0;
    
    sortedHighlights.forEach((highlight, idx) => {
      let start = 0;
      let end = text.length;
      
      if (highlight.verse_end && highlight.verse_end > highlight.verse_start) {
        if (verse.verse === highlight.verse_start) {
          start = highlight.start_offset || 0;
          end = text.length;
        } else if (verse.verse === highlight.verse_end) {
          start = 0;
          end = highlight.end_offset || text.length;
        } else {
          start = 0;
          end = text.length;
        }
      } else {
        start = highlight.start_offset || 0;
        end = highlight.end_offset || text.length;
      }
      
      // Safety bounds
      start = Math.max(0, Math.min(start, text.length));
      end = Math.max(0, Math.min(end, text.length));
      
      // Prevent infinite loops or backward substrings
      if (start < lastIndex) {
        start = lastIndex;
      }
      if (end < start) {
        end = start;
      }
      
      const colorObj = highlightColors.find(c => c.name === highlight.color);
      const color = colorObj?.color || "#FEF08A";
      
      // Add text before highlight
      if (start > lastIndex) {
        parts.push(
          <span key={`text-${idx}`}>{text.substring(lastIndex, start)}</span>
        );
      }
      
      // Add highlighted text
      if (end > start) {
        parts.push(
          <mark 
            key={`highlight-${highlight.id || idx}`}
            style={{ 
              backgroundColor: color,
              padding: '2px 0',
              borderRadius: '2px'
            }}
          >
            {text.substring(start, end)}
          </mark>
        );
      }
      
      lastIndex = Math.max(lastIndex, end);
    });
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(
        <span key="text-end">{text.substring(lastIndex)}</span>
      );
    }
    
    return <>{parts}</>;
  };

  const saveNote = (verseNum) => {
    const key = getHighlightKey(verseNum);
    setUserNotes(prev => {
      const newNotes = { ...prev };
      if (noteText.trim()) {
        newNotes[key] = { text: noteText, createdAt: new Date().toISOString() };
      } else {
        delete newNotes[key];
      }
      localStorage.setItem("bible-notes", JSON.stringify(newNotes));
      return newNotes;
    });
    setActiveNoteVerse(null);
    setNoteText("");
    toast.success("Note enregistrée");
  };

  const goToChapter = (newChapter) => {
    if (newChapter >= 1 && currentBook && newChapter <= currentBook.chapters) {
      navigate(`/bible/${bookId}/${newChapter}`);
      window.scrollTo(0, 0);
    }
  };

  const navigateToBook = (newBookId) => {
    navigate(`/bible/${newBookId}/1`);
    setBooksOpen(false);
    window.scrollTo(0, 0);
  };

  const chapterText = useMemo(() => {
    return verses.map(v => v.text).join(' ');
  }, [verses]);

  const renderSidebarBook = (book) => {
    const isExpanded = selectedSidebarBook === book.id;
    const isCurrentBook = book.id === bookId;

    return (
      <div key={book.id} className="space-y-1">
        <button
          type="button"
          onClick={() => setSelectedSidebarBook(isExpanded ? null : book.id)}
          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-200 font-body text-sm touch-manipulation ${
            isCurrentBook
              ? "bg-paper text-rubric-red font-semibold"
              : "text-ink-primary hover:bg-paper-dark hover:text-rubric-red"
          }`}
        >
          <span>{book.name}</span>
          <ChevronRight className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-90 text-rubric-red" : "text-ink-secondary"}`} />
        </button>
        {isExpanded && (
          <div className="pl-2 pr-1 py-2 my-1 bg-paper-dark/60 rounded-lg border border-border/40">
            <p className="text-[11px] font-medium text-ink-secondary mb-1.5 px-1">Chapitres ({book.chapters}) :</p>
            <div className="grid grid-cols-5 gap-1.5 max-h-48 overflow-y-auto p-1">
              {Array.from({ length: book.chapters }, (_, i) => i + 1).map((ch) => {
                const isCurrentChapter = isCurrentBook && ch === chapterNum;
                return (
                  <button
                    key={ch}
                    type="button"
                    onClick={() => {
                      navigate(`/bible/${book.id}/${ch}`);
                      setBooksOpen(false);
                      window.scrollTo(0, 0);
                    }}
                    className={`h-8 rounded text-xs font-medium transition-all flex items-center justify-center touch-manipulation ${
                      isCurrentChapter
                        ? "bg-rubric-red text-white font-bold shadow-sm"
                        : "bg-paper text-ink-primary hover:bg-rubric-red/10 hover:text-rubric-red border border-border/60"
                    }`}
                  >
                    {ch}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-paper animate-fade-in">
      {/* Top navigation */}
      <div className="sticky top-14 sm:top-16 z-30 bg-paper/95 backdrop-blur-sm border-b border-border md:hidden">
        <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between gap-1 sm:gap-2">
            {/* Book selector */}
            <Sheet open={booksOpen} onOpenChange={setBooksOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" className="text-ink-primary hover:text-rubric-red font-body text-sm sm:text-base px-2 sm:px-3">
                  <Book className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" strokeWidth={1.5} />
                  <span className="truncate max-w-[100px] sm:max-w-none">{currentBook?.name || "Sélectionner"}</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[85vw] max-w-sm bg-paper-dark p-0">
                <SheetHeader className="p-4 sm:p-6 border-b border-border">
                  <SheetTitle className="font-heading text-ink-primary">Livres de la Bible</SheetTitle>
                </SheetHeader>
                <ScrollArea className="h-[calc(100vh-100px)]">
                  <div className="p-4">
                    <h3 className="font-heading text-sm text-ink-secondary uppercase tracking-wider mb-3">Ancien Testament</h3>
                    <div className="space-y-1 mb-6">
                      {books.ancien_testament.map(renderSidebarBook)}
                    </div>
                    <Separator className="my-4" />
                    <h3 className="font-heading text-sm text-ink-secondary uppercase tracking-wider mb-3">Nouveau Testament</h3>
                    <div className="space-y-1">
                      {books.nouveau_testament.map(renderSidebarBook)}
                    </div>
                  </div>
                </ScrollArea>
              </SheetContent>
            </Sheet>

            {/* Chapter navigation */}
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={() => goToChapter(chapterNum - 1)} disabled={chapterNum <= 1} className="text-ink-primary hover:text-rubric-red disabled:opacity-30 h-9 w-9">
                <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
              </Button>
              <span className="font-decorative text-ink-primary text-sm sm:text-base min-w-[60px] sm:min-w-[80px] text-center">Ch. {chapter}</span>
              <Button variant="ghost" size="icon" onClick={() => goToChapter(chapterNum + 1)} disabled={!currentBook || chapterNum >= currentBook.chapters} className="text-ink-primary hover:text-rubric-red disabled:opacity-30 h-9 w-9">
                <ChevronRight className="h-5 w-5" strokeWidth={1.5} />
              </Button>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={() => openGardePageDialog()} className="text-ink-primary hover:text-rubric-red h-9 w-9">
                      <BookmarkPlus className="h-5 w-5" strokeWidth={1.5} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Sauvegarder un garde-page</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <AudioPlayer text={chapterText} isPremium={isPremium} onPremiumRequired={() => navigate("/premium")} />
              
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-ink-primary hover:text-rubric-red h-9 w-9">
                    <Settings className="h-5 w-5" strokeWidth={1.5} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 sm:w-80 bg-paper border-border" align="end">
                  <div className="space-y-4 sm:space-y-6">
                    <div>
                      <Label className="text-sm font-body text-ink-primary mb-3 block">Taille du texte</Label>
                      <div className="flex items-center gap-2 sm:gap-3">
                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setFontSize(Math.max(14, fontSize - 2))}><Minus className="h-4 w-4" /></Button>
                        <Slider value={[fontSize]} min={14} max={28} step={2} onValueChange={([val]) => setFontSize(val)} className="flex-1" />
                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setFontSize(Math.min(28, fontSize + 2))}><Plus className="h-4 w-4" /></Button>
                      </div>
                      <p className="text-xs text-ink-secondary mt-2 text-center">{fontSize}px</p>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {hideNumbers ? <EyeOff className="h-4 w-4 text-ink-secondary" /> : <Eye className="h-4 w-4 text-ink-secondary" />}
                        <Label className="text-sm font-body text-ink-primary">Masquer numéros</Label>
                      </div>
                      <Switch checked={hideNumbers} onCheckedChange={setHideNumbers} />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {hideTitles ? <EyeOff className="h-4 w-4 text-ink-secondary" /> : <Eye className="h-4 w-4 text-ink-secondary" />}
                        <Label className="text-sm font-body text-ink-primary">Masquer titres</Label>
                      </div>
                      <Switch checked={hideTitles} onCheckedChange={setHideTitles} />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between bg-amber-50 p-3 rounded-lg -mx-1">
                      <div className="flex items-center gap-2">
                        <Bell className={`h-4 w-4 ${focusMode ? 'text-amber-600' : 'text-ink-secondary'}`} />
                        <div>
                          <Label className="text-sm font-body text-ink-primary">Mode concentration</Label>
                          <p className="text-xs text-ink-secondary">Bloque les notifications</p>
                        </div>
                      </div>
                      <Switch checked={focusMode} onCheckedChange={setFocusMode} />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className={`h-4 w-4 ${showComments ? 'text-rubric-red' : 'text-ink-secondary'}`} />
                        <div>
                          <Label className="text-sm font-body text-ink-primary">Commentaires Fillion</Label>
                          <p className="text-xs text-ink-secondary">Notes sous les versets (Premium)</p>
                        </div>
                      </div>
                      <Switch
                        data-testid="toggle-comments-desktop"
                        checked={showComments}
                        onCheckedChange={(v) => {
                          setShowComments(v);
                        }}
                      />
                    </div>
                    <Separator />
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Crown className="h-4 w-4 text-gold-leaf" />
                        <span className="text-sm font-body text-ink-primary font-semibold">Options Premium</span>
                      </div>
                      {!isPremium && (
                        <Button onClick={() => navigate("/premium")} className="w-full bg-gold-leaf hover:bg-gold-leaf/90 text-ink-primary">
                          <Crown className="h-4 w-4 mr-2" />Débloquer Premium
                        </Button>
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </div>

      {/* Chapter content */}
      <div className="md:flex md:gap-6 md:max-w-7xl md:mx-auto">
        {/* Sidebar navigation - Hidden on mobile, visible on tablet+ */}
        <aside className="hidden md:block md:w-64 lg:w-72 md:sticky md:top-20 md:self-start md:h-[calc(100vh-6rem)] md:overflow-y-auto md:px-4 md:py-6 md:border-r md:border-border">
          <div className="mb-6">
            <h3 className="font-heading text-sm text-ink-secondary uppercase tracking-wider mb-3">Ancien Testament</h3>
            <div className="space-y-1 mb-6">
              {books.ancien_testament.map(renderSidebarBook)}
            </div>
            <Separator className="my-4" />
            <h3 className="font-heading text-sm text-ink-secondary uppercase tracking-wider mb-3">Nouveau Testament</h3>
            <div className="space-y-1">
              {books.nouveau_testament.map(renderSidebarBook)}
            </div>
          </div>
        </aside>

        {/* Main content area */}
        <main className="flex-1 px-4 sm:px-6 md:px-8 py-6 sm:py-8 max-w-3xl md:max-w-4xl mx-auto md:mx-0">
          {/* Tablet+ header with navigation */}
          <div className="hidden md:flex md:items-center md:justify-between md:mb-6 md:pb-4 md:border-b md:border-border">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => goToChapter(chapterNum - 1)} disabled={chapterNum <= 1} className="text-ink-primary hover:text-rubric-red disabled:opacity-30">
                <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
              </Button>
              <div className="text-center">
                <h1 className="font-heading text-2xl lg:text-3xl text-ink-primary">{currentBook?.name}</h1>
                <p className="font-decorative text-rubric-red text-base lg:text-lg">Chapitre {chapter}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => goToChapter(chapterNum + 1)} disabled={!currentBook || chapterNum >= currentBook.chapters} className="text-ink-primary hover:text-rubric-red disabled:opacity-30">
                <ChevronRight className="h-5 w-5" strokeWidth={1.5} />
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => openGardePageDialog()} className="text-ink-primary hover:text-rubric-red">
                <BookmarkPlus className="h-5 w-5" strokeWidth={1.5} />
              </Button>
              <AudioPlayer text={chapterText} isPremium={isPremium} onPremiumRequired={() => navigate("/premium")} />
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-ink-primary hover:text-rubric-red">
                    <Settings className="h-5 w-5" strokeWidth={1.5} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 bg-paper border-border" align="end">
                  <div className="space-y-6">
                    <div>
                      <Label className="text-sm font-body text-ink-primary mb-3 block">Taille du texte</Label>
                      <div className="flex items-center gap-3">
                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setFontSize(Math.max(14, fontSize - 2))}><Minus className="h-4 w-4" /></Button>
                        <Slider value={[fontSize]} min={14} max={28} step={2} onValueChange={([val]) => setFontSize(val)} className="flex-1" />
                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setFontSize(Math.min(28, fontSize + 2))}><Plus className="h-4 w-4" /></Button>
                      </div>
                      <p className="text-xs text-ink-secondary mt-2 text-center">{fontSize}px</p>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4 text-ink-secondary" />
                        <Label className="text-sm font-body cursor-pointer">Masquer les numéros</Label>
                      </div>
                      <Switch checked={hideNumbers} onCheckedChange={setHideNumbers} />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-ink-secondary" />
                        <Label className="text-sm font-body cursor-pointer">Masquer les titres</Label>
                      </div>
                      <Switch checked={hideTitles} onCheckedChange={setHideTitles} />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div>
                          <Label className="text-sm font-body cursor-pointer block">Mode Focus</Label>
                          <p className="text-xs text-ink-secondary mt-1">Affichage minimal</p>
                        </div>
                      </div>
                      <Switch checked={focusMode} onCheckedChange={setFocusMode} />
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className={`h-4 w-4 ${showComments ? 'text-rubric-red' : 'text-ink-secondary'}`} />
                        <div>
                          <Label className="text-sm font-body cursor-pointer block">Commentaires Fillion</Label>
                          <p className="text-xs text-ink-secondary mt-1">Notes sous chaque verset (Premium)</p>
                        </div>
                      </div>
                      <Switch
                        data-testid="toggle-comments-mobile"
                        checked={showComments}
                        onCheckedChange={(v) => {
                          setShowComments(v);
                        }}
                      />
                    </div>
                    <Separator />
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Crown className="h-4 w-4 text-gold-leaf" />
                        <span className="text-sm font-body text-ink-primary font-semibold">Options Premium</span>
                      </div>
                      {!isPremium && (
                        <Button onClick={() => navigate("/premium")} className="w-full bg-gold-leaf hover:bg-gold-leaf/90 text-ink-primary">
                          <Crown className="h-4 w-4 mr-2" />Débloquer Premium
                        </Button>
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Mobile header - Only visible on mobile */}
          <div className="md:hidden">
        {!hideTitles && (
          <header className="text-center mb-8 sm:mb-12">
            <h1 className="font-heading text-2xl sm:text-3xl text-ink-primary mb-2">{currentBook?.name}</h1>
            <p className="font-decorative text-rubric-red text-base sm:text-lg">Chapitre {chapter}</p>
            <div className="mt-6 sm:mt-8 flex items-center justify-center"><span className="font-decorative text-gold-leaf px-4">✦</span></div>
          </header>
        )}
          </div>

        {loading ? (
          <div className="text-center py-12"><p className="font-body text-ink-secondary italic">Chargement...</p></div>
        ) : (
          <div className="font-body text-ink-primary" style={{ fontSize: `${fontSize}px`, lineHeight: fontSize <= 18 ? 1.8 : 2 }}>
            {introduction && chapterNum === 1 && (
              <div className="mb-8 pb-6 border-b border-border">
                <button onClick={() => setShowIntro(!showIntro)} className="flex items-center gap-2 text-rubric-red hover:text-ink-primary transition-colors font-heading text-base sm:text-lg">
                  <FileText className="h-5 w-5" />{showIntro ? "Masquer l'introduction" : "Lire l'introduction"}
                </button>
                {showIntro && (
                  <div className="mt-4 p-4 sm:p-6 bg-paper-dark rounded-lg">
                    <h3 className="font-heading text-lg sm:text-xl text-ink-primary mb-4">Introduction</h3>
                    <div className="font-body text-ink-secondary whitespace-pre-line leading-relaxed text-sm sm:text-base">{introduction}</div>
                  </div>
                )}
              </div>
            )}

            {verses.map((verse) => {
              const highlights = getHighlights(verse.verse);
              const hasHighlight = highlights.length > 0;
              return (
                <div key={verse.verse} className="mb-4 sm:mb-6 transition-colors duration-500" data-testid={`verse-${verse.verse}`}>
                  <p 
                    className="group relative rounded px-1 -mx-1 transition-colors select-text"
                    onMouseUp={captureSelection}
                    onTouchEnd={captureSelection}
                  >
                    {!hideNumbers && <sup className="font-decorative text-rubric-red mr-1.5 text-xs select-none">{verse.verse}</sup>}
                    <span data-verse-text={verse.verse}>{renderVerseWithHighlights(verse)}</span>
                    <span className="inline-flex items-center gap-1.5 ml-2 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                      <Popover open={highlightMenuVerse === verse.verse} onOpenChange={(open) => {
                        if (open) {
                          // Capture selection before opening menu
                          captureSelection();
                          setHighlightMenuVerse(verse.verse);
                        } else {
                          setHighlightMenuVerse(null);
                        }
                      }}>
                        <PopoverTrigger asChild>
                          <button 
                            className="align-middle p-1 rounded hover:bg-paper-dark touch-manipulation"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Capture selection when clicking
                              captureSelection();
                            }}
                          >
                            <Highlighter className={`h-4 w-4 ${hasHighlight ? 'text-gold-leaf' : 'text-ink-secondary'}`} strokeWidth={1.5} />
                          </button>
                        </PopoverTrigger>
                        {highlightMenuVerse === verse.verse && (
                          <PopoverContent className="w-auto p-2" side="top">
                            <div className="flex gap-1.5">
                              {highlightColors.map((c) => (
                                <button key={c.name} onClick={() => setHighlight(verse.verse, c.name, c.color)} className="w-7 h-7 rounded-full border-2 border-transparent hover:border-ink-primary transition-colors touch-manipulation" style={{ backgroundColor: c.color }} title={c.label} />
                              ))}
                              {hasHighlight && (
                                <button onClick={() => setHighlight(verse.verse, null, null)} className="w-7 h-7 rounded-full border border-ink-secondary flex items-center justify-center hover:bg-paper-dark touch-manipulation" title="Retirer">
                                  <X className="h-3 w-4" />
                                </button>
                              )}
                            </div>
                          </PopoverContent>
                        )}
                      </Popover>
                      <button onClick={() => { setActiveNoteVerse(activeNoteVerse === verse.verse ? null : verse.verse); setNoteText(getNote(verse.verse)?.text || ""); }} className="align-middle p-1 rounded hover:bg-paper-dark touch-manipulation" title="Note">
                        <StickyNote className={`h-4 w-4 ${getNote(verse.verse) ? 'text-gold-leaf' : 'text-ink-secondary'}`} strokeWidth={1.5} />
                      </button>
                      <button onClick={() => openGardePageDialog(verse.verse)} className="align-middle p-1 rounded hover:bg-paper-dark touch-manipulation" title="Ajouter un garde-page">
                        <BookmarkPlus className="h-4 w-4 text-ink-secondary hover:text-gold-leaf" strokeWidth={1.5} />
                      </button>
                      <button onClick={() => shareVerse(verse.verse)} data-testid={`share-verse-${verse.verse}-button`} className="align-middle p-1 rounded hover:bg-paper-dark touch-manipulation" title="Partager ce verset">
                        <Share2 className="h-4 w-4 text-ink-secondary hover:text-rubric-red" strokeWidth={1.5} />
                      </button>
                    </span>
                  </p>
                  {activeNoteVerse === verse.verse && (
                    <div className="mt-2 p-3 bg-paper-dark rounded-lg border border-border">
                      <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Écrivez votre note..." className="w-full p-2 text-sm border border-border rounded-lg resize-none bg-paper focus:outline-none focus:ring-1 focus:ring-rubric-red" rows={3} />
                      <div className="flex justify-end gap-2 mt-2">
                        <Button variant="ghost" size="sm" onClick={() => setActiveNoteVerse(null)}>Annuler</Button>
                        <Button size="sm" onClick={() => saveNote(verse.verse)} className="bg-rubric-red hover:bg-rubric-red/90 text-white">Enregistrer</Button>
                      </div>
                    </div>
                  )}
                  {getNote(verse.verse) && activeNoteVerse !== verse.verse && (
                    <div className="mt-2 p-3 bg-amber-50 border-l-4 border-gold-leaf rounded-r text-sm cursor-pointer hover:bg-amber-100 transition-colors" onClick={() => { setActiveNoteVerse(verse.verse); setNoteText(getNote(verse.verse)?.text || ""); }}>
                      <p className="text-ink-secondary italic">{getNote(verse.verse).text}</p>
                    </div>
                  )}
                  {showComments && verse.has_comment && verse.comment && (
                    <div data-testid={`fillion-comment-${verse.verse}`} className="mt-3 mb-4 p-3 sm:p-4 bg-parchment-light/60 dark:bg-card/40 rounded-lg border-l-4 border-rubric-red shadow-sm text-sm">
                      <div className="flex items-center gap-2 mb-1.5">
                        <FileText className="h-3.5 w-3.5 text-rubric-red" />
                        <span className="text-xs uppercase tracking-wider text-rubric-red font-semibold font-heading">
                          Commentaire de L.-Cl. Fillion — Verset {verse.verse}
                        </span>
                      </div>
                      <div className="pl-1">
                        <FormattedCommentary text={verse.comment} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom navigation */}
        <div className="flex justify-between items-center mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-border">
          <Button variant="ghost" onClick={() => goToChapter(chapterNum - 1)} disabled={chapterNum <= 1} className="text-ink-primary hover:text-rubric-red font-body text-sm sm:text-base">
            <ChevronLeft className="h-4 w-4 mr-1 sm:mr-2" strokeWidth={1.5} /><span className="hidden sm:inline">Chapitre </span>précédent
          </Button>
          <Button variant="ghost" onClick={() => goToChapter(chapterNum + 1)} disabled={!currentBook || chapterNum >= currentBook.chapters} className="text-ink-primary hover:text-rubric-red font-body text-sm sm:text-base">
            <span className="hidden sm:inline">Chapitre </span>suivant<ChevronRight className="h-4 w-4 ml-1 sm:ml-2" strokeWidth={1.5} />
          </Button>
        </div>
        </main>
      </div>

      {/* Floating selection toolbar (single or multi-verse selection) */}
      {selectionSegments && selectionSegments.length > 0 && (
        <div
          data-testid="selection-toolbar"
          className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 bg-paper border border-border rounded-full shadow-lg px-3 py-2 flex items-center gap-2 animate-fade-in"
        >
          <span className="text-xs font-decorative text-ink-secondary whitespace-nowrap px-1">
            {selectionSegments.length > 1
              ? `Versets ${selectionSegments[0].verseNum}–${selectionSegments[selectionSegments.length - 1].verseNum}`
              : `Verset ${selectionSegments[0].verseNum}`}
          </span>
          {highlightColors.map((c) => (
            <button
              key={c.name}
              data-testid={`selection-highlight-${c.name}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setHighlight(selectionSegments[0].verseNum, c.name, c.color)}
              className="w-6 h-6 rounded-full border-2 border-transparent hover:border-ink-primary transition-colors touch-manipulation flex-shrink-0"
              style={{ backgroundColor: c.color }}
              title={`Surligner en ${c.label.toLowerCase()}`}
            />
          ))}
          <button
            data-testid="selection-share-button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={shareSelection}
            className="p-1.5 rounded-full hover:bg-paper-dark touch-manipulation"
            title="Partager la sélection"
          >
            <Share2 className="h-4 w-4 text-ink-secondary" strokeWidth={1.5} />
          </button>
          <button
            data-testid="selection-close-button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={clearSelection}
            className="p-1.5 rounded-full hover:bg-paper-dark touch-manipulation"
            title="Fermer"
          >
            <X className="h-4 w-4 text-ink-secondary" />
          </button>
        </div>
      )}

      {/* Garde-page Dialog */}
      <Dialog open={showGardePageDialog} onOpenChange={setShowGardePageDialog}>
        <DialogContent className="bg-paper sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Sauvegarder un garde-page</DialogTitle>
            <DialogDescription>Enregistrez votre position actuelle pour reprendre votre lecture plus tard.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="garde-page-name" className="text-sm font-body">Nom du garde-page</Label>
            <Input id="garde-page-name" value={gardePageName} onChange={(e) => setGardePageName(e.target.value)} placeholder="Ex: Ma lecture du soir" className="mt-2" />
            <p className="text-xs text-ink-secondary mt-2">
              Position: {currentBook?.name} - Chapitre {chapter}
              {gardePageVerse && <span className="font-semibold text-gold-leaf"> - Verset {gardePageVerse}</span>}
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowGardePageDialog(false)}>Annuler</Button>
            <Button onClick={() => saveGardePage()} className="bg-rubric-red hover:bg-rubric-red/90 text-white"><Bookmark className="h-4 w-4 mr-2" />Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReaderPage;
